import { mergeAttributes, Node } from "@tiptap/core";
import isEmpty from "lodash-es/isEmpty";

import { convertStringToFragment } from "@/editor/extensions/ai/utils.ts";
import { setNodeViewAttrs } from "@/editor/adapters/utils";
import { findNodeWithPosition } from "@/editor/utils/nodes";
import { preprocessContent } from "@/editor/utils/preprocessContent.ts";

import { NWSectionProps, NWSectionStatus, NWSectionType, ProVariantProps } from "./models";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    nwSection: {
      /**
       * Adds a new section node to the editor
       */
      addNWSection: (options: NWSectionProps) => ReturnType;
      /**
       * Sets the content of a section node
       */
      setSectionContent: (sectionId: string, content: string) => ReturnType;
      /**
       * Unlocks a pro section
       */
      updateProSection: (sectionId: string, options: Partial<ProVariantProps>) => ReturnType;
    };
  }
}

export const NWSectionAdapter = Node.create<NWSectionProps>({
  name: "nw-section",
  group: "block",
  content: "block+",
  draggable: false,
  selectable: false,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: element => element.getAttribute("id"),
      },
      // PRO type attributes
      "content-id": {
        default: null,
        parseHTML: element => element.getAttribute("content-id"),
      },
      type: {
        default: NWSectionType.SECTION,
        parseHTML: element => element.getAttribute("type"),
      },
      status: {
        default: NWSectionStatus.LOCKED,
        parseHTML: element => element.getAttribute("status"),
      },
      // SECTION type attributes
      stepid: {
        default: null,
        parseHTML: element => element.getAttribute("stepid"),
      },
      name: {
        default: null,
        parseHTML: element => element.getAttribute("name"),
      },
      heading: {
        default: null,
        parseHTML: element => element.getAttribute("heading"),
      },
      isopen: {
        default: true,
        parseHTML: element => {
          const value = element.getAttribute("isopen");
          const elementType = element.getAttribute("type") as NWSectionType;

          if (isEmpty(elementType)) return true;

          if (elementType === NWSectionType.EXTRA_TASK) {
            return !isEmpty(value) ? value === "true" : false;
          }

          return !isEmpty(value) ? value === "true" : true;
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "nw-section",
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ["nw-section", mergeAttributes(HTMLAttributes), 0];
  },

  addNodeView() {
    return ({ node, editor, getPos }) => {
      const isOpenableType = node.attrs.type === NWSectionType.SECTION || node.attrs.type === NWSectionType.EXTRA_TASK;

      const dom = document.createElement("nw-section");
      setNodeViewAttrs(dom, { ...node.attrs });

      const onToggleIsOpen = ((event: CustomEvent) => {
        const isOpen = event.detail.isOpen;

        editor.commands.command(({ tr }) => {
          tr.setNodeAttribute(getPos(), "isopen", isOpen);
          return true;
        });
      }) as EventListener;

      if (isOpenableType) {
        dom.addEventListener("toggle-isopen", onToggleIsOpen);
      }

      return {
        dom,
        contentDOM: dom,
        ...(isOpenableType
          ? {
              update: updatedNode => {
                if (updatedNode.type !== node.type) return false;
                setNodeViewAttrs(dom, updatedNode.attrs);
                return true;
              },
              destroy: () => {
                if (isOpenableType) {
                  dom.removeEventListener("toggle-isopen", onToggleIsOpen);
                }
              },
            }
          : {}),
      };
    };
  },

  addCommands() {
    return {
      addNWSection:
        options =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: options,
            content: [
              {
                type: "paragraph",
              },
            ],
          });
        },

      updateProSection:
        (sectionId, options) =>
        ({ state, chain }) => {
          const node = findNodeWithPosition(state, "nw-section", sectionId);
          if (node == null) {
            console.error("Node not found for sectionId:", sectionId);
            return false;
          }

          chain()
            .setMeta("addToHistory", false)
            .command(({ tr }) => {
              tr.setNodeMarkup(node.pos, undefined, { ...node.node.attrs, ...options });
              return true;
            })
            .run();

          return true;
        },

      setSectionContent:
        (sectionId: string, content: string) =>
        ({ chain, state, view }) => {
          if (view == null) {
            return false;
          }

          const node = findNodeWithPosition(state, "nw-section", sectionId);

          if (node == null) {
            console.error("Node not found for sectionId:", sectionId);
            return false;
          }

          const insertPos = node.pos + 1;
          const fragment = convertStringToFragment(preprocessContent(content), state.schema);

          return chain()
            .setMeta("addToHistory", false)
            .command(({ tr }) => {
              tr.insert(insertPos, fragment);
              return true;
            })
            .run();
        },
    };
  },
});
